import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Input,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Autocomplete } from "@react-google-maps/api";
import PhoneInput from "react-phone-input-2";
import { useAddUserApiMutation, useEditUserMutation } from "../../../services/users";
import { UploadMedia } from "../../../utils/uploadMedia";
import { errorToast, successToast } from "../../../helpers";
import { useLazyGetSpecificAgentByIdQuery } from "../../../services/agents";
import { generateEncryptedKeyBody } from "../../../utils/crypto";
import { CommonBody } from "../../../types/General";
import Loader from "../../../helpers/constants/Loader";
import { isNumber } from "../../../utils/validation";

const AddAffiliateMembers = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [dialCode, setPhoneCode] = useState("+971");
  const [editUSERbyId] = useEditUserMutation();
  const [profilePicture, setProfilePicture] = useState("");
  const [imageError, setImageError] = useState("");
  const [addUser] = useAddUserApiMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ByIdDetails, setByIdDetails] = useState<any>(null);
  const [fetchApi] = useLazyGetSpecificAgentByIdQuery();

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    try {
      if (!file) {
        formik.setFieldValue("profile_picture", "");
        setProfilePicture("");
        return;
      }

      const res = await UploadMedia(file);
      if (res?.statusCode === 200) {
        const imageUrl = res?.data[0]?.location;
        formik.setFieldValue("profile_picture", imageUrl);
        setProfilePicture(imageUrl);
        successToast("Image uploaded successfully.");
      } else {
        errorToast(res?.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      errorToast("Error uploading image. Please try again.");
    }
  };

  const fetchDataById = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await fetchApi({ userId: id }).unwrap();
      if (response.statusCode === 200) {
        setByIdDetails(response?.data || []);
        setPhoneCode(response?.data?.dialCode || "");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: ByIdDetails?.name || "",
      email: ByIdDetails?.email || "",
      phoneNo: ByIdDetails?.phoneNo || "",
      profile_picture: ByIdDetails?.image || profilePicture,
      affiliateCommission: ByIdDetails?.affiliateCommission || "",
      dialCode: dialCode,

    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        )
        .matches(
          /^[^\uD800-\uDBFF\uD800-\uDBFF]+$/,
          "Emojis are not allowed in the email address"
        ),
      name: Yup.string()
        .required("Full name is required")
        .min(2, "Minimum 2 characters are required")
        .max(80, "Maximum 80 characters are allowed")
        .matches(
          /^[a-zA-Z\s]+$/,
          "Full name must contain only letters and spaces"
        ),
      phoneNo: Yup.string()
        .required("Phone number is required")
        .min(7, "Phone number must be at least 7 characters")
        .max(20, "Phone number must be at most 20 characters"),
      affiliateCommission: Yup.string()
        .required("Commision is required"),


    }),
    onSubmit: async (values, { setSubmitting }) => {
      formik.setSubmitting(true);
      let body: any = {
        name: values.name || "",
        email: values.email || "",
        phoneNo: values.phoneNo || "",
        image: values?.profile_picture || "",
        dialCode: dialCode,
        affiliateCommission: Number(values?.affiliateCommission) || 0
      };

      if (!id) {
        body = { ...body, role: 7 };
      }
      console.log(body, "body");

      let encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      if (id) {
        try {
          setIsLoading(true);
          const response = await editUSERbyId({
            id,
            body: encryptedBody,
          }).unwrap();
          if (response?.statusCode === 200) {
            successToast("Member updated successfully");
            navigate("/affiliate-members");
          }
          setIsLoading(false);
        } catch (error: any) {
          setIsLoading(false);
          console.error(error);
          errorToast(error?.data?.message);
        } finally {
          setIsLoading(false);
          setSubmitting(false);
        }
      } else {
        try {
          setIsLoading(true);
          const response = await addUser({ body: encryptedBody }).unwrap();
          if (response?.statusCode === 200) {
            successToast("Member added successfully");
            navigate("/affiliate-members");
          }
          setIsLoading(false);
        } catch (error: any) {
          console.error(error);
          setIsLoading(false);
          errorToast(error?.data?.message);
        } finally {
          setIsLoading(false);
          setSubmitting(false);
        }
      }
    },
  });

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phoneNo", phone?.replace(country?.dialCode, ""));
    setPhoneCode(
      country?.dialCode.includes("+")
        ? country?.dialCode
        : "+" + country?.dialCode
    );
  };

  useEffect(() => {
    if (ByIdDetails) {
      formik.setValues({
        name: ByIdDetails?.name || "",
        email: ByIdDetails?.email || "",
        phoneNo: ByIdDetails?.phoneNo || "",
        profile_picture: ByIdDetails?.image || "",
        dialCode: ByIdDetails?.dialCode || "",
        affiliateCommission: ByIdDetails?.affiliateCommission || ""
      });
    }
  }, [ByIdDetails]);

  useEffect(() => {
    if (id) {
      fetchDataById(id);
    }
  }, [id]);

  return (
    <div>
      <Loader isLoad={isLoading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{id ? `Edit Member` : `Add Member`}</h1>
          <Button
            className="btn btn_primary"
            onClick={() => navigate("/affiliate-members")}
          >
            Back
          </Button>
        </div>
        <Card className="cards">
          <form onSubmit={formik.handleSubmit} >
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography className="custom_label">Image</Typography>
                  {formik.values.profile_picture ? (
                    <div className="upload_image_preview">
                      <CardMedia
                        component="img"
                        style={{ borderRadius: 0 }}
                        image={formik.values.profile_picture}
                        alt="photo"
                      />
                      <CancelIcon
                        onClick={(e) => {
                          e.preventDefault();
                          formik.setFieldValue("profile_picture", "");
                        }}
                      />
                    </div>
                  ) : (
                    <Box className="upload_image">
                      <label htmlFor="icon-button-file">
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={handleImageUpload}
                        />
                        <Button component="span" className="upload_image_btn">
                          <img src="/static/images/user_placeholder.png" alt="" />
                          <CameraAltIcon />
                        </Button>
                      </label>

                    </Box>
                  )}
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">Full Name</Typography>
                  <TextField
                    hiddenLabel
                    type="text"
                    name="name"
                    className="text_field"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Name"
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    helperText={
                      formik.touched.name && formik.errors.name
                        ? formik.errors.name.toString()
                        : ""
                    }
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">Email</Typography>
                  <TextField
                    className="text_field"
                    hiddenLabel
                    type={"email"}
                    name="email"
                    variant="outlined"
                    fullWidth
                    placeholder="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.email && formik.errors.email
                        ? formik.errors.email.toString()
                        : ""
                    }
                  ></TextField>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">Phone Number</Typography>
                  <PhoneInput
                    value={dialCode + formik.values.phoneNo}
                    country={"ae"}
                    placeholder="0 (000) 000-000"
                    enableSearch={true}
                    inputStyle={{ width: "100%" }}
                    onChange={(phone, country) =>
                      handleChangePhone(phone, country)
                    }
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.phoneNo && formik.errors.phoneNo ? (
                    <h6 className="err_msg">
                      {formik.touched.phoneNo &&
                        formik.errors.phoneNo.toString()}
                    </h6>
                  ) : (
                    ""
                  )}
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">Commission(%)</Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    className="text_field"
                    name="affiliateCommission"
                    variant="outlined"
                    inputProps={{ maxLength: 4 }}
                    fullWidth
                    placeholder="Enter here"
                    value={formik.values.affiliateCommission}
                    onChange={(val) => {
                      if (val.target.value === " ") {
                        return;
                      } else if (isNumber(val.target.value)) {
                        formik.setFieldValue('affiliateCommission', val.target.value)
                      }
                    }}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.affiliateCommission && formik.errors.affiliateCommission
                        ? formik.errors.affiliateCommission.toString()
                        : ""
                    }

                  />
                </Grid>






              </Grid>
              <div className="form_btn">
                <Button
                  size="large"
                  type="submit"
                  className="btn btn_primary"
                >
                  Save
                </Button>
              </div>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default AddAffiliateMembers;
